import { z } from "zod";
import {
    Counterparty,
    FederalTaxClassification,
} from "@common/types/counterparty";
import {
    optionalCounterpartyEmailSchema,
    optionalCounterpartyNoteSchema,
    optionalCounterpartyTextSchema,
} from "@common/schemas/counterparty.schema";

export const DEFAULT_COUNTRY = "US";

export const counterpartyFormSchema = z.object({
    name: z.string().min(1, "Display name is required"),
    companyName: optionalCounterpartyTextSchema,
    cc: optionalCounterpartyEmailSchema,
    bcc: optionalCounterpartyEmailSchema,
    title: optionalCounterpartyTextSchema,
    firstName: optionalCounterpartyTextSchema,
    middleName: optionalCounterpartyTextSchema,
    lastName: optionalCounterpartyTextSchema,
    suffix: optionalCounterpartyTextSchema,
    federalTaxClassification: z
        .nativeEnum(FederalTaxClassification)
        .nullable()
        .optional(),
    note: optionalCounterpartyNoteSchema,
});

export interface FormSchema {
    name: string;
    companyName: string | null;
    email: string | null;
    cc: string | null;
    bcc: string | null;
    phoneNumber: string | null;
    website: string | null;
    title: string | null;
    firstName: string | null;
    middleName: string | null;
    lastName: string | null;
    suffix: string | null;
    street: string | null;
    street2: string | null;
    city: string | null;
    postalCode: string | null;
    state: string | null;
    country: string | null;
    isEligible1099: boolean | null;
    federalTaxClassification: FederalTaxClassification | null;
    note: string | null;
}

type AddressField =
    | "street"
    | "street2"
    | "city"
    | "postalCode"
    | "state"
    | "country";

export type CounterpartyFormSubmission = Omit<FormSchema, AddressField> &
    Partial<Pick<FormSchema, AddressField>>;

const COMPANY_UPDATE_FIELDS = [
    "name",
    "companyName",
    "cc",
    "bcc",
    "website",
    "isEligible1099",
    "federalTaxClassification",
    "note",
] as const;

export function toCompanyCounterpartyUpdate(
    data: CounterpartyFormSubmission,
): Pick<FormSchema, (typeof COMPANY_UPDATE_FIELDS)[number]> {
    return {
        name: data.name,
        companyName: data.companyName,
        cc: data.cc,
        bcc: data.bcc,
        website: data.website,
        isEligible1099: data.isEligible1099,
        federalTaxClassification: data.federalTaxClassification,
        note: data.note,
    };
}

export function toAddressScalars(
    data: FormSchema,
): Pick<FormSchema, AddressField> {
    return {
        street: data.street,
        street2: data.street2,
        city: data.city,
        postalCode: data.postalCode,
        state: data.state,
        country: data.country,
    };
}

export function omitEmptyAddress(data: FormSchema): CounterpartyFormSubmission {
    const { street, street2, city, postalCode, state, country, ...rest } = data;
    const address = { street, street2, city, postalCode, state, country };
    const hasAddress = Object.values(address).some(Boolean);
    return hasAddress ? { ...rest, ...address } : rest;
}

export function omitContactAndAddress(
    data: FormSchema,
): CounterpartyFormSubmission {
    const { street, street2, city, postalCode, state, country, ...rest } = data;
    return { ...rest, email: null, phoneNumber: null };
}

export function buildCounterpartyFormInitialValues({
    counterparty,
    initialValues,
}: {
    counterparty?: Counterparty;
    initialValues?: Partial<FormSchema>;
}): FormSchema {
    return {
        name: counterparty?.name ?? initialValues?.name ?? "",
        companyName:
            counterparty?.companyName ?? initialValues?.companyName ?? "",
        email: counterparty?.email ?? initialValues?.email ?? "",
        cc: counterparty?.cc ?? initialValues?.cc ?? "",
        bcc: counterparty?.bcc ?? initialValues?.bcc ?? "",
        phoneNumber:
            counterparty?.phoneNumber ?? initialValues?.phoneNumber ?? "",
        website: counterparty?.website ?? initialValues?.website ?? "",
        title: counterparty?.title ?? initialValues?.title ?? "",
        firstName: counterparty?.firstName ?? initialValues?.firstName ?? "",
        middleName: counterparty?.middleName ?? initialValues?.middleName ?? "",
        lastName: counterparty?.lastName ?? initialValues?.lastName ?? "",
        suffix: counterparty?.suffix ?? initialValues?.suffix ?? "",
        street: counterparty?.street ?? initialValues?.street ?? "",
        street2: counterparty?.street2 ?? initialValues?.street2 ?? "",
        city: counterparty?.city ?? initialValues?.city ?? "",
        postalCode: counterparty?.postalCode ?? initialValues?.postalCode ?? "",
        state: counterparty?.state ?? initialValues?.state ?? "",
        country:
            counterparty?.country ?? initialValues?.country ?? DEFAULT_COUNTRY,
        isEligible1099:
            counterparty?.isEligible1099 ??
            initialValues?.isEligible1099 ??
            null,
        federalTaxClassification:
            counterparty?.federalTaxClassification ??
            initialValues?.federalTaxClassification ??
            null,
        note: counterparty?.note ?? initialValues?.note ?? "",
    };
}
